const EPLOG = -1 * Math.log10(Number.EPSILON);

/************************************************************************************************************
 * Cleans floating point errors. For example, in node: 1.5 * 35.55 = 53.324999999999996, even though it
 * should just be 53.325. If we wanted to round the initial result to the nearest hundredth, we'd get 53.32,
 * even though that's wrong. So we clean the FPEs by first rounding at the 10th decimal point.
 ************************************************************************************************************/
export const cleanFloatingPointErrors = (value: number): number => {
  const num = Number(value);
  const mag = Math.abs(num); // Logs are undefined for negatives
  if (mag < 1e-6) return 0; // Under a threshold, just say it's 0 so that this function returns 0 on an input of floating point error itself

  // Larger numbers have less distinction between values at the same decimal place, so let's account for that
  // Example: there's literally less of a physical difference between 10000000.01 and 10000000.02 than there is between 1.01 and 1.02 given how floats work
  // Can use properties of logs to speed this up by pre-calculating the log of EPSILON
  // This is equivalent to Math.floor(Math.log10(1 / (mag * Number.EPSILON)))
  const prec = Math.floor(EPLOG - Math.log10(mag));

  // Lob off a couple more decimal places to be a bit more aggressive
  const fixer = Math.pow(10, prec - 2);
  return Math.round(fixer * num) / fixer;
};

/************************************************************************************************************
 * Rounds a number to the specified number of places. Defaults to 2 decimal places.
 ************************************************************************************************************/
export const roundTo = (value: number, places: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 = 2): number => {
  // Important to first clean floating point errors. Example:
  // 1.5 * 35.55 should equal 53.325, which we would round to 53.33 if rounding to 2 places
  // Due to floating point errors, 1.5 * 35.55 = 53.324999999999996, which rounds down to 53.32! No good!
  const multiplier = 10 ** places;
  return Math.round(cleanFloatingPointErrors(multiplier * value)) / multiplier;
};
